import ConveyorBelt from './traps/ConveyorBelt';
import ParticleTile from './traps/ParticleTile';
import CrateObject from './physics/CrateObject';
import CrusherTrap from './traps/CrusherTrap';
import ObjectTile from './traps/ObjectTile';
import lavaFrag from './shaders/lava.frag';
import lavaVert from './shaders/lava.vert';
import SpikeTrap from './traps/SpikeTrap';
import GoalTile from './traps/GoalTile';
import GemTile from './traps/GemTile';
import tile from './environment/tile';
import Actor from './physics/Actor';
import common from './common';
import utils from './utils';
import THREEParticleEmitter from './particles/THREEParticleEmitter';

export default class LevelImporter {
	constructor(grid) {
		this.signals = {
			loadcomplete: new P3.utils.MiniSignal()
		};
		this._tilesToMerge = {
			tiles : [],
			walls : [],
			fog   : []
		};
		this._uniqueLinks = new Map();
		this._tileproperties = null;
		this._lavaSound = null;
		this._fireSound = null;
		this._steamSound = null;
		this._tiletypes = null;
		this._mapHeight = 0;
		this._mapWidth = 0;
		this._level = null;
		this._grid = grid;
		this._objectTiles = [];

		this.RATIO = 16;
		this.TILE_HEIGHT = 64; // px
		this.TILE_WIDTH = 64; // px

		this._linkSender = null;
		this._linkReceivers = [];
	}

	load(what) {
		this._level = P3.assets.json(what);

		this._tileproperties = this._level.tilesets[0].tileproperties;
		this._tiletypes = this._level.tilesets[0].tiles || this._level.tilesets[0].tilepropertytypes;

		const level = new THREE.Group();

		level.tiles = [];
		level.walls = [];
		level.traps = [];
		level.crates = [];
		level.resetables = [];
		level.goal = null;
		level.player = null;
		level.cameraPath = null;
		level.tilesToUpdate = [];

		for (const layer in this._level.layers) {
			if (this._level.layers[layer].name === 'CameraPath') {
				level.cameraPath = this.constructCameraPath(this._level.layers[layer], level);
				continue;
			}

			for (const tile of this._level.layers[layer].objects) {
				const options = {
					id         : tile.id,
					gid        : tile.gid,
					name       : tile.name,
					type       : this.getTileType(tile.gid) || '',
					width      : tile.width / this.RATIO,
					height     : tile.height / this.RATIO,
					rotation   : tile.rotation || 0,
					x          : tile.x / this.RATIO,
					y          : tile.y / this.RATIO,
					properties : tile.properties || {}
				};

				if (!tile.visible || options.type.includes('water')) {
					continue;
				}

				if (this._level.layers[layer].name === 'FogLayer') {
					const mesh = this._createBlackout(options);

					mesh.matrixAutoUpdate = false;
					mesh.updateMatrix();
					level.add(mesh);
					continue;
				}

				if (options.type.includes('blank')) {
					const localX = options.x / this._grid.tileDimensions.width;
					const localY = options.y / this._grid.tileDimensions.height;

					if (localX > this._mapWidth) {
						this._mapWidth = localX;
					}
					if (localX > this._mapHeight) {
						this._mapHeight = localY;
					}

					const gridTile = this._grid.getTileAt(localX, localY);

					gridTile.position3D.x += gridTile.width * 0.5;
					gridTile.position3D.z += gridTile.height * 0.5;

					continue;
				}

				let entity = null;

				if (options.type.includes('tile')) {
					const merge = options.properties.merge === undefined ? true : options.properties.merge;

					entity = this._createTile(options);
					if (options.type.includes('standard') && merge) {
						this._tilesToMerge.tiles.push(entity);
					} else if (options.type.includes('standard') && !merge) {
						level.tilesToUpdate.push(entity);
						level.resetables.push(entity);
						level.add(entity);
					} else if (options.type.includes('goal') || options.type.includes('gem') ||
						options.type.includes('obstacle') || options.type.includes('particles') ||
						options.type.includes('acid')) {
						level.tilesToUpdate.push(entity);
						level.add(entity);

						if (options.type.includes('gem')) {
							level.resetables.push(entity);
						}
					} else if (options.type.includes('object')) {
						level.tilesToUpdate.push(entity);
						level.resetables.push(entity);
						level.add(entity);
					} else {
						level.resetables.push(entity);
						level.add(entity);
					}
				} else if (options.type.includes('trap')) {
					entity = this._createTrap(options);
					level.traps.push(entity);
					level.tilesToUpdate.push(entity);
					level.add(entity);
				} else {
					entity = this._createWall(options);
					level.walls.push(entity);
					this._tilesToMerge.walls.push(entity);
				}

				if (this._level.layers[layer].name === 'Underwater Layer') {
					entity.position.y -= 8;

					entity.updateMatrix();
					entity.type === Actor.TILE && (entity.type = Actor.UNDERWATER_TILE);
				}

				level.tiles.push(entity);

				if (options.type.includes('tile') || options.type.includes('trap') || options.type.includes('wall')) {
					const localX = options.x / this._grid.tileDimensions.width;
					const localY = options.y / this._grid.tileDimensions.height;

					const gridTile = this._grid.getTileAt(localX, localY);

					if (localX > this._mapWidth) {
						this._mapWidth = localX;
					}
					if (localX > this._mapHeight) {
						this._mapHeight = localY;
					}
					if (options.type.includes('goal')) {
						level.goal = gridTile;
					}

					// This checks for walls in tiled. Because walls are usually three tiles wide, the three grid tiles in this game have to be marked as a wall.
					if (options.type.includes('wall')) {
						const walltilewidth = options.width / this._grid.tileDimensions.width;
						const walltileheight = options.height / this._grid.tileDimensions.height;

						for (let i = 0; i < walltilewidth; i++) {
							const wallGridTile = this._grid.getTileAt(localX + i, localY);

							wallGridTile.isWall = true;
							wallGridTile.isUnderwater = wallGridTile.isUnderwater || this._level.layers[layer].name === 'Underwater Layer';
							wallGridTile.types 	= options.type;
							wallGridTile.addObject(entity);
						}

						for (let j = 0; j < walltileheight; j++) {
							const wallGridTile = this._grid.getTileAt(localX, localY - j);

							wallGridTile.isWall = true;
							wallGridTile.isUnderwater = wallGridTile.isUnderwater || this._level.layers[layer].name === 'Underwater Layer';
							wallGridTile.types 	= options.type;
							wallGridTile.addObject(entity);
						}
					}

					gridTile.addObject(entity);
					gridTile.position3D = new THREE.Vector3().copy(entity.position);

					if (options.type.includes('obstacle')) {
						const crate = new CrateObject();

						level.tilesToUpdate.push(crate);
						level.resetables.push(crate);
						gridTile.addObject(crate);
						level.add(crate);
						level.crates.push(crate);

						crate.position.copy(gridTile.position3D);
						crate.position.y += 1.85;
						crate.startParameters.position.copy(crate.position);
					}

					gridTile.isUnderwater = gridTile.isUnderwater || this._level.layers[layer].name === 'Underwater Layer';
					gridTile.isTile = gridTile.isTile || options.type.includes('tile') || options.type.includes('trap');
					gridTile.isWall = gridTile.isWall || options.type.includes('wall');
					gridTile.isBad = options.type.includes('bad') || options.type.includes('trap');
					gridTile.isGoal = options.type.includes('goal');
					gridTile.types = Array.from(new Set(gridTile.types.concat(options.type)));

					entity.tile = gridTile;
					if (entity instanceof Actor) {
						entity.startParameters.position.copy(entity.position);
					}

					entity.matrixAutoUpdate = false;
					entity.updateMatrix();
				}

				if (options.type.includes('player')) {
					level.player = entity;
				}
			}
		}

		// The following code merges all 'standard' tile meshes in the scene and merges them into one. This reduces drawcalls from tileCount to 1.
		for (const tileGroup in this._tilesToMerge) {
			const geoArray = [];
			let material = null;

			this._tilesToMerge[tileGroup].forEach((tile) => {
				const mesh = tile.mesh || tile;

				if (!material) {
					material = mesh.material.clone();// We need their material.
				}

				mesh && mesh.updateMatrixWorld(); // Update all of their transforms.
				tile.updateMatrix();

				const geo = mesh ? mesh.geometry.clone() : tile.geometry.clone(); // Take the geometry and clone it.

				mesh && geo.applyMatrix(mesh.matrixWorld); // Apply their transforms and bake them into the mesh.
				geo.applyMatrix(tile.matrix);

				geoArray.push(geo);
			});

			if (geoArray.length > 0) {
				let mergedGeometry = new THREE.BufferGeometry();

				mergedGeometry = THREE.BufferGeometryUtils.mergeBufferGeometries(geoArray); // Take all the merged geometries and combine them into one.

				const mergedMesh = new THREE.Mesh(mergedGeometry, material);

				mergedMesh.matrixAutoUpdate = false;
				level.add(mergedMesh);

				this._tilesToMerge[tileGroup].length = 0;
			}
		}

		const planeGeo = new THREE.PlaneGeometry(128, 128, 32, 32);
		if (!common.levelSettings.noceiling) {
			const ceilingtexture = common.assets.texture(common.levelSettings.ceilingtexture);

			ceilingtexture.wrapS = ceilingtexture.wrapT = THREE.RepeatWrapping;
			ceilingtexture.repeat.set(32, 32);

			const planeMat = new THREE.MeshBasicMaterial({
				map  : ceilingtexture,
				side : THREE.BackSide
			});
			const ceilingmesh = new THREE.Mesh(planeGeo, planeMat);

			ceilingmesh.dispose = () => {
				planeMat.dispose();
				planeGeo.dispose();
			};

			ceilingmesh.rotateX(-90 * PIXI.DEG_TO_RAD);
			ceilingmesh.position.set(64, 8, 64);
			level.add(ceilingmesh);
		}

		const water = new THREE.Water(planeGeo, {
			color         : 0x748dca, // 0x748dca
			scale         : 16,
			flowDirection : new THREE.Vector3(0, -0.5),
			normalMap0    : common.assets.texture('Water_1_M_Normal'),
			normalMap1    : common.assets.texture('Water_2_M_Normal'),
			textureWidth  : 256,
			textureHeight : 256
		});
		water.rotateX(-90 * PIXI.DEG_TO_RAD);
		water.position.set(64, -0.01, 64);
		water.dispose = () => {
			water.material.dispose();
			planeGeo.dispose();
		};
		level.add(water);

		this.signals.loadcomplete.dispatch(this, level, this._mapWidth + 1, this._mapHeight + 1);
	}

	_createBlackout(options) {
		const y = 8.05;
		const blackGeo = new THREE.PlaneBufferGeometry(options.width, options.height);

		const texture = common.assets.texture(common.levelSettings.ceilingtexture).clone();

		texture.needsUpdate = true;
		texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
		texture.repeat.set(options.width / 4, options.height / 4);

		const blackMat = new THREE.MeshBasicMaterial({color: 0xFFFFFF, map: texture});
		const blackMesh = new THREE.Mesh(blackGeo, blackMat);

		blackMesh.position.set(options.x + (options.width * 0.5), y, options.y - (options.height * 0.5));
		blackMesh.quaternion.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -90 * PIXI.DEG_TO_RAD);

		blackMesh.dispose = () => {
			blackGeo.dispose();
			blackMat.dispose();
		};

		return blackMesh;
	}

	constructCameraPath(layer) {
		const line = layer.objects[0].polyline;
		const points = [];

		for (let i = 0; i < line.length; i++) {
			const x = (line[i].x / this.RATIO) + (layer.objects[0].x / this.RATIO);
			const y = (line[i].y / this.RATIO) + (layer.objects[0].y / this.RATIO);

			points.push(new THREE.Vector3(x, 2.9, y));
		}
		points[points.length - 1].x -= 0.2;

		return points;
	}

	_createTrap(options) {
		const y = options.properties.y || this.getTileProperties(options.gid).y || 0;
		const which = options.properties.type || 'spikes';
		const activated = options.properties.activated || this.getTileProperties(options.gid).activated || false;

		let entity = null;

		switch (which) {
			case 'spikes':
				entity = new SpikeTrap(activated, tile.SPIKE_TRAP);
				break;
			case 'crusher':
				entity = new CrusherTrap(activated, tile.CRUSHER_TRAP);
				break;
			default:
				throw new Error(`No trap type provided for trap ID: ${options.gid}`);
		}
		entity.position.set(options.x + (options.width * 0.5), y, options.y - (options.height * 0.5));
		this.establishLinks(entity, options);

		return entity;
	}

	_createWall(options) {
		const rot = options.properties.rotation || 0;
		const y = options.properties.y || this.getTileProperties(options.gid).y || 0;

		const mesh = common.assets.model('wall_001');

		mesh.material = new THREE.MeshBasicMaterial({
			map: common.assets.texture(common.levelSettings.walltexture)
		});

		mesh.receiveShadow = false;
		mesh.castShadow = true;
		const entity = new Actor(mesh);

		entity.mesh.rotation.z = rot * PIXI.DEG_TO_RAD;

		entity.type = Actor.WALL;
		entity.position.set(options.x + (options.width * 0.5), y, options.y - (options.height * 0.5));

		/* Some personal variables*/
		entity.width = options.width;
		entity.height = 8.4;
		entity.depth = options.height;
		entity.name = options.name;

		return entity;
	}

	_createTile(options) {
		const y = options.properties.y || this.getTileProperties(options.gid).y || 0;

		let mesh = common.assets.model('tile_001');
		let entity = null;
		let matCopy = new THREE.MeshBasicMaterial({
			map: common.assets.texture(common.levelSettings.floortexture)
		});

		if (options.type.includes('conveyor')) {
			const speed = options.properties.speed || this.getTileProperties(options.gid).speed || 500;
			const activated = options.properties.activated;
			const texture = common.assets.texture('lab_conveyor');

			texture.wrapS = THREE.RepeatWrapping;
			texture.wrapT = THREE.RepeatWrapping;
			matCopy = new THREE.MeshBasicMaterial({
				map: texture
			});

			const cubeGeo = new THREE.BoxBufferGeometry(4, 0.2, 4);

			mesh = new THREE.Mesh(cubeGeo, matCopy);
			entity = new ConveyorBelt(mesh, speed, activated);

			if (options.type.includes('right')) {
				mesh.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), 270 * PIXI.DEG_TO_RAD);
				entity.direction = ConveyorBelt.DIR_RIGHT;
			} else if (options.type.includes('down')) {
				mesh.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), 180 * PIXI.DEG_TO_RAD);
				entity.direction = ConveyorBelt.DIR_DOWN;
			} else if (options.type.includes('left')) {
				mesh.quaternion.setFromAxisAngle(new THREE.Vector3(0, 1, 0), 90 * PIXI.DEG_TO_RAD);
				entity.direction = ConveyorBelt.DIR_LEFT;
			} else {
				entity.direction = ConveyorBelt.DIR_UP;
			}

			entity.specialType = Actor.SPECIAL_CONVEYOR;

			mesh.onBeforeRender = () => {
				entity.activated && (texture.offset.y -= 0.0002);
			};
		} else if (options.type.includes('particles')) {
			const type = options.properties.particle_effect || this.getTileProperties(options.gid).particle_effect || 'particle_fire_horizontal';
			const rotation = options.properties.rotation || this.getTileProperties(options.gid).rotation || 0.0;
			let offset = options.properties.offset || new THREE.Vector3(0, 0, 0);

			if (!(offset instanceof THREE.Vector3)) {
				const offsetValues = offset.split(',').map(Number);

				offset = new THREE.Vector3(...offsetValues);
			}
			entity = new ParticleTile(type, offset);
			entity.rotateY(rotation * PIXI.DEG_TO_RAD);

			if (type.indexOf('fire') > -1 && !this._fireSound) {
				this._fireSound = P3.audio.playSound(`sfx_firetrap_loop_0${P3.math.randomInt(0, 1)}`, {loop: true});
			}

			if (type.indexOf('steam') > -1 && !this._steamSound) {
				this._steamSound = P3.audio.playSound('sfx_steamtrap_loop_01', {loop: true});
			}
		} else if (options.type.includes('goal')) {
			entity = new GoalTile();
			entity.startAnim();
		} else if (options.type.includes('gem')) {
			entity = new GemTile();
		} else if (options.type.includes('object')) {
			let offset = options.properties.modelOffset || new THREE.Vector3(0, 0, 0);
			let scale = options.properties.scale || new THREE.Vector3(1, 1, 1);
			const filename = options.properties.filename;
			const rotation = options.properties.modelRotation || 0;
			const base = options.properties.base || true;

			if (!filename) {
				throw new Error('No filename supplied for ObjectTile');
			}

			if (!(offset instanceof THREE.Vector3)) {
				const offsetValues = offset.split(',').map(Number);

				offset = new THREE.Vector3(...offsetValues);
			}
			if (!(scale instanceof THREE.Vector3)) {
				const scaleValues = scale.split(',').map(Number);

				scale = new THREE.Vector3(...scaleValues);
			}
			options.type.includes('object') && (entity = new ObjectTile(mesh, filename, base, offset, rotation, scale));
		} else if (options.type.includes('lava')) {
			const uniforms = {
				fogDensity : {value: 0},
				fogColor   : {value: new THREE.Vector3(0, 0, 0)},
				time       : {value: 1.0},
				uvScale    : {value: new THREE.Vector2(1, 1)},
				texture1   : {value: common.assets.texture('cloud')},
				texture2   : {value: common.assets.texture('mine_lava')}
			};

			uniforms.texture1.value.wrapS = uniforms.texture1.value.wrapT = THREE.RepeatWrapping;
			uniforms.texture2.value.wrapS = uniforms.texture2.value.wrapT = THREE.RepeatWrapping;

			if (!options.properties.puddle) {
				matCopy = new THREE.ShaderMaterial({
					uniforms       : uniforms,
					vertexShader   : lavaVert,
					fragmentShader : lavaFrag,
					side           : THREE.BackSide
				});

				const cubeGeo = new THREE.PlaneBufferGeometry(4, 4);

				mesh = new THREE.Mesh(cubeGeo, matCopy);

				mesh.onBeforeRender = () => {
					uniforms.time.value += 0.01;
				};

				mesh.rotateX(90 * PIXI.DEG_TO_RAD);
				mesh.position.y += 0.0004;
			}
			entity = new Actor(mesh);

			if (options.properties.puddle) {
				const decalGeo = new THREE.PlaneBufferGeometry(1.8, 1.8);
				const decalMat = new THREE.MeshStandardMaterial({map: common.assets.texture('lava_puddle'), side: THREE.FrontSide, transparent: true});
				const decalMesh = new THREE.Mesh(decalGeo, decalMat);

				decalMesh.rotateX(270 * PIXI.DEG_TO_RAD);
				decalMesh.position.y = 0.81;
				mesh.add(decalMesh);
			}

			// we only want one lava audio to prevent sound spam
			if (!this._lavaSound) {
				this._lavaSound = P3.audio.playSound('sfx_steamtrap_loop_01', {loop: true});
			}
		} else if (options.type.includes('ice')) {
			const cubeGeo = new THREE.BoxBufferGeometry(4, 0.2, 4);

			mesh = new THREE.Mesh(cubeGeo, matCopy);
			mesh.position.y -= 0.05;

			entity = new Actor(mesh);
			matCopy = new THREE.MeshStandardMaterial({
				color: 0x779faa
			});
		} else if (options.type.includes('acid')) {
			if (!options.properties.puddle) {
				matCopy = new THREE.MeshBasicMaterial({
					map: common.assets.texture('acid')
				});

				const planeGeo = new THREE.PlaneBufferGeometry(4, 4);

				mesh = new THREE.Mesh(planeGeo, matCopy);
				mesh.rotateX(270 * PIXI.DEG_TO_RAD);
				entity = new Actor(mesh);
				mesh.position.y += 0.0004;		// prevent z-fighting

				const acidParticles = new THREEParticleEmitter(P3.assets.json('particle_acid_patch_bubbles'), true);
				mesh.add(acidParticles);
				acidParticles.update();
				common.particles3D.push(acidParticles);
			}

			entity = new Actor(mesh);

			if (options.properties.puddle) {
				const decalGeo = new THREE.PlaneBufferGeometry(1.7, 1.7);
				const decalMat = new THREE.MeshBasicMaterial({map: common.assets.texture('acid_puddle'), side: THREE.BackSide, transparent: true});
				const decalMesh = new THREE.Mesh(decalGeo, decalMat);

				decalMesh.position.z = -0.85;
				mesh.add(decalMesh);
		   }
		} else {
			entity = new Actor(mesh);
		}

		this.establishLinks(entity, options);
		mesh.material = matCopy;

		entity.type = Actor.TILE;
		entity.position.set(options.x + ((options.width * 0.5)), y, options.y - ((options.height * 0.5)));

		/* Some personal variables*/
		entity.width = options.width;
		entity.height = 0.4;
		entity.depth = options.height;
		entity.name = options.name;

		return entity;
	}

	establishLinks(entity, options) {
		const links = options.properties.link || null;

		if (links) {
			const params = links.split(',');

			if (this._uniqueLinks.has(params[1])) {		// Do we have a link with already?
				const keyValue = this._uniqueLinks.get(params[1]);	// Get the link
				const newlink = {sender: null, receiver: null};

				if (params[0] === 'sender') {	// If our current object is a sender, set it and copy the receiver from the previous link
					newlink.sender = entity;
					this._linkSender = entity;
					if (keyValue.receiver) {
						newlink.receiver = keyValue.receiver;
					}

					for (let i = 0; i < this._linkReceivers.length; i++) {
						entity.addLink(entity, this._linkReceivers[i]);
						this._linkReceivers[i].addLink(entity, this._linkReceivers[i]);
					}
				} else {						// If our current object is a receiver, set it and copy the sender from the previous link
					newlink.receiver = entity;
					this._linkReceivers.push(entity);
					if (keyValue.sender) {
						newlink.sender = keyValue.sender;
					}

					if (newlink.sender && newlink.receiver) {
						entity.addLink(newlink.sender, entity);
						newlink.sender.addLink(newlink.sender, entity);
					}
				}

				this._uniqueLinks.set(params[1], newlink);		// set the new link
			} else {		// If we dont have a previous link, set it.
				const newlink = {sender: null, receiver: null};

				if (params[0] === 'sender') {
					newlink.sender = entity;
				} else {
					this._linkReceivers.push(entity);
					newlink.receiver = entity;
				}

				this._uniqueLinks.set(params[1], newlink);
			}
		}
	}

	getTileProperties(gid) {
		return this._tileproperties[gid - 1];
	}

	getTileType(gid) {
		const id = (gid - 1).toString();

		return this._tiletypes[id] && this._tiletypes[id].type ? this._tiletypes[id].type.split('|') : null;
	}

	destroy() {
		if (this._lavaSound) {
			this._lavaSound.stop();
		}
		if (this._fireSound) {
			this._fireSound.stop();
		}
		if (this._steamSound) {
			this._steamSound.stop();
		}
	}
}
